import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

interface Props {
  title?: string;
  children: ReactNode;
}

/** Tooltip pinned to the horizontal center of its trigger, directly above it. */
const AboveTooltip = ({ title, children }: Props) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [box, setBox] = useState<{ left: number; top: number } | null>(null);

  const place = (el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    setBox({
      left: rect.left + rect.width / 2,
      top: rect.top,
    });
  };

  useEffect(() => {
    if (!anchor) return;
    const sync = () => place(anchor);
    window.addEventListener("scroll", sync, true);
    window.addEventListener("resize", sync);
    return () => {
      window.removeEventListener("scroll", sync, true);
      window.removeEventListener("resize", sync);
    };
  }, [anchor]);

  return (
    <>
      <span
        className="inline-flex w-fit shrink-0"
        onMouseEnter={(e) => {
          setAnchor(e.currentTarget);
          place(e.currentTarget);
        }}
        onMouseLeave={() => {
          setAnchor(null);
          setBox(null);
        }}
      >
        {children}
      </span>
      {title && box
        ? createPortal(
            <div
              role="tooltip"
              className="pointer-events-none fixed z-[2000]"
              style={{
                left: box.left,
                top: box.top - 6,
                transform: "translate(-50%, -100%)",
              }}
            >
              <div className="relative whitespace-nowrap rounded bg-[#616161] px-2 py-1 text-[11px] leading-tight text-white">
                {title}
                <span
                  aria-hidden
                  className="absolute left-1/2 top-full -translate-x-1/2 border-[5px] border-transparent border-t-[#616161]"
                />
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
};

export default AboveTooltip;
